import { useState, useEffect } from 'react';
import { attendanceController } from '../../controllers/attendancecontroller';
import { employeeController } from '../../controllers/employeeController';
import Attendance from '../../models/Attendance';
import { Plus, Search, Download, Eye, Edit2, Trash2, X, AlertCircle, ChevronUp, ChevronDown, Calendar, Clock, CheckCircle, XCircle, Users } from 'lucide-react';

const AttendancePage = () => {
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterDate, setFilterDate] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [formData, setFormData] = useState({
    employeeId: '',
    date: new Date().toISOString().split('T')[0],
    checkIn: '',
    checkOut: '',
    status: 'Present',
    workHours: 0,
    remarks: ''
  });
  const [formErrors, setFormErrors] = useState([]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => { 
    fetchAttendance(); 
    fetchEmployees();
  }, []);

  useEffect(() => {
    let result = [...attendanceRecords];
    
    // Search filter
    if (searchQuery) {
      result = result.filter(record => {
        const employee = employees.find(e => e.id === record.employeeId || e.employeeId === record.employeeId);
        if (employee) {
          return (
            employee.getFullName().toLowerCase().includes(searchQuery.toLowerCase()) ||
            employee.employeeId.toLowerCase().includes(searchQuery.toLowerCase()) ||
            record.date.includes(searchQuery)
          );
        }
        return false;
      });
    }
    
    // Status filter
    if (filterStatus !== 'All') {
      result = result.filter(r => r.status === filterStatus);
    }
    
    // Date filter
    if (filterDate) {
      result = result.filter(r => r.date === filterDate);
    }
    
    // Sorting
    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'employeeName') {
        const empA = employees.find(e => e.id === a.employeeId || e.employeeId === a.employeeId);
        const empB = employees.find(e => e.id === b.employeeId || e.employeeId === b.employeeId);
        aVal = empA ? empA.getFullName() : '';
        bVal = empB ? empB.getFullName() : '';
      }
      
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    setFilteredRecords(result);
    setCurrentPage(1);
  }, [attendanceRecords, employees, searchQuery, filterStatus, filterDate, sortField, sortDirection]);

  const fetchAttendance = async () => {
    setLoading(true);
    const result = await attendanceController.getAll();
    if (result.success) {
      setAttendanceRecords(result.data);
    } else {
      alert('Error: ' + result.message);
    }
    setLoading(false);
  };

  const fetchEmployees = async () => {
    const result = await employeeController.getAll();
    if (result.success) {
      setEmployees(result.data);
    }
  };

  const getEmployeeById = (employeeId) => {
    return employees.find(e => e.id === employeeId || e.employeeId === employeeId);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Auto-calculate work hours when both checkIn and checkOut are set
      if ((name === 'checkIn' || name === 'checkOut') && updated.checkIn && updated.checkOut) {
        const tempRecord = new Attendance(updated);
        updated.workHours = tempRecord.calculateWorkHours();
      }
      
      return updated;
    });
    setFormErrors([]);
  };

  const handleAdd = () => {
    setFormData({
      employeeId: '',
      date: new Date().toISOString().split('T')[0],
      checkIn: '',
      checkOut: '',
      status: 'Present',
      workHours: 0,
      remarks: ''
    });
    setFormErrors([]);
    setShowAddModal(true);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);
    
    const result = await attendanceController.create(formData);
    if (result.success) {
      setShowAddModal(false);
      fetchAttendance();
      alert('✅ Attendance record created!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    setFormSubmitting(false);
  };

  const handleEdit = (record) => {
    setSelectedRecord(record);
    setFormData({
      employeeId: record.employeeId,
      date: record.date,
      checkIn: record.checkIn,
      checkOut: record.checkOut,
      status: record.status,
      workHours: record.workHours,
      remarks: record.remarks || ''
    });
    setFormErrors([]);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);
    
    const result = await attendanceController.update(selectedRecord.id, formData);
    if (result.success) {
      setShowEditModal(false);
      fetchAttendance();
      alert('✅ Attendance record updated!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    setFormSubmitting(false);
  };

  const handleView = (record) => {
    setSelectedRecord(record);
    setShowViewModal(true);
  };

  const handleDelete = async (record) => {
    try {
      const employee = getEmployeeById(record.employeeId);
      const employeeName = employee ? employee.getFullName() : 'this employee';
      
      const isConfirmed = window.confirm(
        `Delete attendance record for ${employeeName} on ${record.date}?`
      );
      
      if (!isConfirmed) return;
      
      const result = await attendanceController.delete(record.id);
      
      if (result?.success) {
        await fetchAttendance();
        alert('✅ Attendance record deleted!');
      } else {
        alert(result?.message || '❌ Failed to delete attendance record');
      }
    } catch (error) {
      console.error('Delete attendance error:', error);
      alert('❌ Something went wrong while deleting attendance record');
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExport = async () => {
    const filters = {};
    if (filterStatus !== 'All') filters.status = filterStatus;
    if (filterDate) filters.date = filterDate;
    
    const result = await attendanceController.export(filters, 'csv');
    if (result.success) {
      const blob = new Blob([result.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      alert('✅ Attendance records exported!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRecords = filteredRecords.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);

  // Calculate statistics
  const today = new Date().toISOString().split('T')[0];
  const todayRecords = attendanceRecords.filter(r => r.date === today);
  
  const stats = {
    total: todayRecords.length,
    present: todayRecords.filter(r => r.status === 'Present').length,
    absent: todayRecords.filter(r => r.status === 'Absent').length,
    onLeave: todayRecords.filter(r => r.status === 'Leave').length
  };

  const styles = {
    container: { padding: 0 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' },
    title: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b', margin: 0 },
    button: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s' },
    secondaryButton: { backgroundColor: '#fff', color: '#1e293b', border: '1px solid #e2e8f0' },
    statsBar: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' },
    statCard: { backgroundColor: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    statLabel: { fontSize: '14px', color: '#64748b', marginBottom: '8px' },
    statValue: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b' },
    filterBar: { display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' },
    searchBox: { flex: 1, minWidth: '250px', position: 'relative' },
    searchInput: { width: '100%', padding: '10px 10px 10px 40px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' },
    searchIcon: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' },
    select: { padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', backgroundColor: '#fff', cursor: 'pointer' },
    card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { backgroundColor: '#f8fafc', padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#475569', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none' },
    td: { padding: '16px', borderBottom: '1px solid #e2e8f0', fontSize: '14px', color: '#1e293b' },
    badge: { display: 'inline-block', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
    actionButton: { padding: '6px', backgroundColor: 'transparent', border: 'none', borderRadius: '6px', cursor: 'pointer', transition: 'background-color 0.2s' },
    pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderTop: '1px solid #e2e8f0' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
    modalContent: { backgroundColor: '#fff', borderRadius: '12px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflow: 'auto' },
    modalHeader: { padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    modalTitle: { fontSize: '20px', fontWeight: '600', color: '#1e293b', margin: 0 },
    modalBody: { padding: '24px' },
    formGroup: { marginBottom: '20px' },
    label: { display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' },
    input: { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'border-color 0.2s' },
    errorList: { backgroundColor: '#fee2e2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' },
    errorItem: { color: '#dc2626', fontSize: '14px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' },
    modalFooter: { padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Attendance Management</h1>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={handleExport}>
            <Download size={18} /> Export
          </button>
          <button style={styles.button} onClick={handleAdd}>
            <Plus size={18} /> Mark Attendance
          </button>
        </div>
      </div>

      <div style={styles.statsBar}>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Today's Total</div>
            <Users size={24} color="#3b82f6" />
          </div>
          <div style={styles.statValue}>{stats.total}</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Present</div>
            <CheckCircle size={24} color="#10b981" />
          </div>
          <div style={{ ...styles.statValue, color: '#10b981' }}>{stats.present}</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Absent</div>
            <XCircle size={24} color="#ef4444" />
          </div>
          <div style={{ ...styles.statValue, color: '#ef4444' }}>{stats.absent}</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>On Leave</div>
            <Calendar size={24} color="#f59e0b" />
          </div>
          <div style={{ ...styles.statValue, color: '#f59e0b' }}>{stats.onLeave}</div>
        </div>
      </div>

      <div style={styles.filterBar}>
        <div style={styles.searchBox}>
          <Search size={18} color="#94a3b8" style={styles.searchIcon} />
          <input 
            type="text" 
            placeholder="Search by employee name or ID..." 
            style={styles.searchInput} 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
          />
        </div>
        <input 
          type="date" 
          style={styles.select} 
          value={filterDate} 
          onChange={(e) => setFilterDate(e.target.value)} 
        />
        <select style={styles.select} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="All">All Status</option>
          <option value="Present">Present</option>
          <option value="Absent">Absent</option>
          <option value="Half Day">Half Day</option>
          <option value="Leave">Leave</option>
          <option value="Holiday">Holiday</option>
        </select>
      </div>

      <div style={styles.card}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading attendance records...</div>
        ) : currentRecords.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No attendance records found. Click "Mark Attendance" to create one.</div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th} onClick={() => handleSort('date')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Date {sortField === 'date' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th} onClick={() => handleSort('employeeName')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Employee {sortField === 'employeeName' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th}>Check-In</th>
                  <th style={styles.th}>Check-Out</th>
                  <th style={styles.th}>Work Hours</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentRecords.map((record) => {
                  const employee = getEmployeeById(record.employeeId);
                  return (
                    <tr key={record.id} style={{ cursor: 'pointer' }}>
                      <td style={styles.td}>
                        <div style={{ fontWeight: '500' }}>{new Date(record.date).toLocaleDateString()}</div>
                      </td>
                      <td style={styles.td} onClick={() => handleView(record)}>
                        <div style={{ fontWeight: '500' }}>{employee ? employee.getFullName() : 'Unknown'}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{employee ? employee.employeeId : record.employeeId}</div>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Clock size={14} color="#64748b" />
                          {record.checkIn || '-'}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Clock size={14} color="#64748b" />
                          {record.checkOut || '-'}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={{ fontWeight: '500' }}>{record.workHours.toFixed(1)}h</div>
                      </td>
                      <td style={styles.td}>
                        <span style={{ 
                          ...styles.badge, 
                          backgroundColor: `${record.getStatusColor()}20`, 
                          color: record.getStatusColor() 
                        }}>
                          {record.status}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button 
                            style={styles.actionButton} 
                            onClick={(e) => { e.stopPropagation(); handleView(record); }} 
                            title="View"
                          >
                            <Eye size={18} color="#64748b" />
                          </button>
                          <button 
                            style={styles.actionButton} 
                            onClick={(e) => { e.stopPropagation(); handleEdit(record); }} 
                            title="Edit"
                          >
                            <Edit2 size={18} color="#3b82f6" />
                          </button>
                          <button 
                            style={styles.actionButton} 
                            onClick={(e) => { e.stopPropagation(); handleDelete(record); }} 
                            title="Delete"
                          >
                            <Trash2 size={18} color="#ef4444" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div style={styles.pagination}>
              <div style={{ fontSize: '14px', color: '#64748b' }}>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredRecords.length)} of {filteredRecords.length} records
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  style={{ ...styles.button, ...styles.secondaryButton }} 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: '14px', color: '#64748b' }}>
                  Page {currentPage} of {totalPages}
                </div>
                <button 
                  style={{ ...styles.button, ...styles.secondaryButton }} 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div style={styles.modal} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{showAddModal ? 'Mark Attendance' : 'Edit Attendance'}</h2>
              <button 
                style={{ ...styles.actionButton, padding: '8px' }} 
                onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={showAddModal ? handleAddSubmit : handleEditSubmit}>
              <div style={styles.modalBody}>
                {formErrors.length > 0 && (
                  <div style={styles.errorList}>
                    {formErrors.map((error, index) => (
                      <div key={index} style={styles.errorItem}>
                        <AlertCircle size={14} />
                        {error}
                      </div>
                    ))}
                  </div>
                )}

                <div style={styles.formGroup}>
                  <label style={styles.label}>Employee *</label>
                  <select 
                    name="employeeId" 
                    style={styles.input} 
                    value={formData.employeeId} 
                    onChange={handleInputChange} 
                    required
                    disabled={showEditModal}
                  >
                    <option value="">Select Employee</option>
                    {employees.filter(e => e.status === 'Active').map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {emp.getFullName()} ({emp.employeeId})
                      </option>
                    ))}
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Date *</label>
                  <input 
                    type="date" 
                    name="date" 
                    style={styles.input} 
                    value={formData.date} 
                    onChange={handleInputChange} 
                    required 
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Check-In</label>
                    <input 
                      type="time" 
                      name="checkIn" 
                      style={styles.input} 
                      value={formData.checkIn} 
                      onChange={handleInputChange} 
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Check-Out</label>
                    <input 
                      type="time" 
                      name="checkOut" 
                      style={styles.input} 
                      value={formData.checkOut} 
                      onChange={handleInputChange} 
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Status *</label>
                  <select 
                    name="status" 
                    style={styles.input} 
                    value={formData.status} 
                    onChange={handleInputChange} 
                    required
                  >
                    <option value="Present">Present</option>
                    <option value="Absent">Absent</option>
                    <option value="Half Day">Half Day</option>
                    <option value="Leave">Leave</option>
                    <option value="Holiday">Holiday</option>
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Work Hours</label>
                  <input 
                    type="number" 
                    name="workHours" 
                    style={styles.input} 
                    value={formData.workHours} 
                    onChange={handleInputChange} 
                    step="0.1"
                    min="0"
                    readOnly
                  />
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                    Automatically calculated from check-in and check-out times
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Remarks</label>
                  <textarea 
                    name="remarks" 
                    style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }} 
                    value={formData.remarks} 
                    onChange={handleInputChange}
                    placeholder="Optional notes about this attendance record..."
                  />
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button 
                  type="button" 
                  style={{ ...styles.button, ...styles.secondaryButton }} 
                  onClick={() => { setShowAddModal(false); setShowEditModal(false); }} 
                  disabled={formSubmitting}
                >
                  Cancel
                </button>
                <button type="submit" style={styles.button} disabled={formSubmitting}>
                  {formSubmitting ? 'Saving...' : (showAddModal ? 'Mark Attendance' : 'Update Attendance')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedRecord && (
        <div style={styles.modal} onClick={() => setShowViewModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Attendance Details</h2>
              <button style={{ ...styles.actionButton, padding: '8px' }} onClick={() => setShowViewModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div style={styles.modalBody}>
              {(() => {
                const employee = getEmployeeById(selectedRecord.employeeId);
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Employee</div>
                      <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                        {employee ? employee.getFullName() : 'Unknown'}
                      </div>
                      {employee && (
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                          {employee.employeeId} • {employee.department}
                        </div>
                      )}
                    </div>

                    <div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Date</div>
                      <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                        {new Date(selectedRecord.date).toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Check-In Time</div>
                      <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                        {selectedRecord.checkIn || 'Not recorded'}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Check-Out Time</div>
                      <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                        {selectedRecord.checkOut || 'Not recorded'}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Work Hours</div>
                      <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                        {selectedRecord.workHours.toFixed(1)} hours
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Status</div>
                      <span style={{ 
                        ...styles.badge, 
                        backgroundColor: `${selectedRecord.getStatusColor()}20`, 
                        color: selectedRecord.getStatusColor() 
                      }}>
                        {selectedRecord.status}
                      </span>
                    </div>
                  </div>
                );
              })()}

              {selectedRecord.remarks && (
                <div style={{ marginTop: '24px' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>Remarks</div>
                  <div style={{ 
                    fontSize: '14px', 
                    color: '#1e293b', 
                    padding: '12px', 
                    backgroundColor: '#f8fafc', 
                    borderRadius: '8px' 
                  }}>
                    {selectedRecord.remarks}
                  </div>
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button 
                style={{ ...styles.button, ...styles.secondaryButton }} 
                onClick={() => { setShowViewModal(false); handleEdit(selectedRecord); }}
              >
                <Edit2 size={16} /> Edit
              </button>
              <button style={styles.button} onClick={() => setShowViewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendancePage;